
import React, { useState, useEffect } from "react";
import { useParams, NavLink } from "react-router-dom";
import axiosHttp from "../../Config";
import Loader from "../../Components/Loader";
import ProfileView from "../../Components/ProfileView";

const extractListPayload = (payload) => {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data?.data)) {
    return payload.data.data;
  }

  if (Array.isArray(payload?.data?.followers)) {
    return payload.data.followers;
  }

  if (Array.isArray(payload?.data?.following)) {
    return payload.data.following;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.followers)) {
    return payload.followers;
  }

  if (Array.isArray(payload?.following)) {
    return payload.following;
  }

  return [];
};

const normalizeFollowList = (payload) => {
  const list = extractListPayload(payload);

  return list.map((entry, index) => {
    const displayNameCandidate = [
      entry?.fullName,
      entry?.full_name,
      entry?.name,
      entry?.displayName,
      entry?.username,
      entry?.email,
      [
        entry?.firstName || entry?.firstname || entry?.fName || "",
        entry?.lastName || entry?.lastname || entry?.lName || "",
      ]
        .map((part) => (part ? String(part).trim() : ""))
        .filter(Boolean)
        .join(" "),
    ].find((value) => value && String(value).trim());

    const displayName = displayNameCandidate || `User ${index + 1}`;

    const subtitleCandidate = [
      entry?.headline,
      entry?.bio,
      entry?.about,
      entry?.designation,
      entry?.role,
      entry?.title,
    ].find((value) => value && String(value).trim());

    const avatar =
      entry?.picUrl ||
      entry?.profile_pic ||
      entry?.profilePicture ||
      entry?.avatar ||
      entry?.image ||
      "";

    return {
      id:
        entry?.user_id ||
        entry?.id ||
        entry?._id ||
        entry?.uuid ||
        entry?.userId ||
        `follow-${index}`,
      displayName: String(displayName).trim(),
      subtitle: subtitleCandidate ? String(subtitleCandidate).trim() : "",
      avatar,
    };
  });
};

const fetchFollowListWithFallback = async (userId, primaryPath, fallbackPath) => {
  const endpoints = [primaryPath, fallbackPath].filter(Boolean);
  for (const endpoint of endpoints) {
    if (!endpoint) {
      continue;
    }

    try {
      const response = await axiosHttp.get(endpoint);
      if (response.status === 200) {
        return normalizeFollowList(response.data);
      }
    } catch (error) {
      // Continue to the next endpoint on failure
      continue;
    }
  }

  return [];
};

const UserDetail = () => {
  const { id } = useParams();
  const [profileData, setProfileData] = useState(null);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchUserDetail = async () => {
      setLoading(true);

      const profilePromise = axiosHttp
        .get(`profile/${id}`)
        .then((res) => (res.status === 200 ? res.data?.data || null : null))
        .catch(() => null);

      const followersPromise = fetchFollowListWithFallback(
        id,
        `users/${id}/followers`,
        `users/${id}/following`
      );

      const followingPromise = fetchFollowListWithFallback(
        id,
        `users/${id}/following`,
        `users/${id}/follow`
      );

      try {
        const [profileResult, followersResult, followingResult] = await Promise.all([
          profilePromise,
          followersPromise,
          followingPromise,
        ]);

        if (!isMounted) {
          return;
        }

        setProfileData(profileResult);
        setFollowers(followersResult);
        setFollowing(followingResult);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setProfileData(null);
        setFollowers([]);
        setFollowing([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUserDetail();

    return () => {
      isMounted = false;
    };
  }, [id]);

  return loading ? (
    <Loader />
  ) : (
    <div className="container-fluid top-container">
      <div className="col-md-9 post mb-5">
        <div className="card">
          <div className="card-body">
            <NavLink to="../users" className="title ">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="mr-2"
              >
                <path
                  d="M16.6667 10.2285C16.6667 10.5449 16.4316 10.8064 16.1266 10.8478L16.0417 10.8535L5.05508 10.853L9.02424 14.8059C9.26884 15.0495 9.26969 15.4452 9.02614 15.6898C8.80473 15.9122 8.45755 15.9331 8.21249 15.7521L8.14226 15.6917L3.10059 10.6717C3.06835 10.6396 3.04035 10.6049 3.01658 10.5682C3.00986 10.5571 3.00303 10.5458 2.99655 10.5343C2.99059 10.5244 2.98521 10.5142 2.98013 10.5038C2.97307 10.4887 2.96618 10.4731 2.95992 10.4572C2.95483 10.445 2.95059 10.433 2.94671 10.421C2.94211 10.4061 2.93764 10.3902 2.93379 10.3741C2.93093 10.3628 2.92866 10.352 2.92668 10.3411C2.9239 10.3249 2.92154 10.3081 2.91985 10.2912C2.91839 10.2782 2.91752 10.2654 2.91703 10.2526C2.91691 10.2448 2.91675 10.2367 2.91675 10.2285L2.91706 10.2043C2.91754 10.192 2.91838 10.1798 2.91958 10.1675L2.91675 10.2285C2.91675 10.1891 2.9204 10.1505 2.92739 10.1131C2.92901 10.1041 2.93094 10.095 2.93308 10.0858C2.93752 10.067 2.94265 10.0489 2.94855 10.0311C2.95146 10.0223 2.95483 10.0129 2.95844 10.0035C2.96575 9.98471 2.97369 9.96678 2.98242 9.94932C2.98648 9.94111 2.99102 9.93251 2.99578 9.924C3.00359 9.91012 3.01163 9.89693 3.02014 9.88407C3.02614 9.87497 3.03279 9.8655 3.03973 9.85617L3.04514 9.84897C3.06198 9.82697 3.08025 9.80612 3.09981 9.78657L3.10056 9.78599L8.14222 4.76515C8.3868 4.52158 8.78253 4.5224 9.0261 4.76698C9.24753 4.98933 9.26699 5.33659 9.08493 5.58089L9.02427 5.65086L5.05675 9.60301L16.0417 9.60351C16.3869 9.60351 16.6667 9.88333 16.6667 10.2285Z"
                  fill="black"
                  fillOpacity="0.6"
                />
              </svg>
              User profile
            </NavLink>
            <ProfileView
              profileData={profileData}
              followers={followers}
              following={following}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetail;
